import React from 'react';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { AdminChatAssistResult } from '../../../models/admin/AdminChatAssistResult';
import AdminAssistGroundedSuggestion from '../AdminAssistGroundedSuggestion';
import AdminAssistInvestorResponses from '../AdminAssistInvestorResponses';
import { StyledButton } from '../../styles/StyledButtons';
import StyledFieldDescription from '../../styles/StyledFieldDescription';
import { StyledLabel, StyledTypography } from '../../styles/styles';
import { INTENT_CHIP_COLOR } from './veraAdminChatAssistPromptSandboxUtils';

interface AdminAssistPreviewSectionProps {
  sessionId: string;
  markdown: string;
  selectedContact: boolean;
  hasInvestorMessage: boolean;
  assistLoading: boolean;
  assistResult: AdminChatAssistResult | null;
  assistError: string | null;
  promptSynced: boolean;
  intentSynced: boolean;
  onRunAssist: () => void;
}

export default function AdminAssistPreviewSection({
  sessionId,
  markdown,
  selectedContact,
  hasInvestorMessage,
  assistLoading,
  assistResult,
  assistError,
  promptSynced,
  intentSynced,
  onRunAssist,
}: AdminAssistPreviewSectionProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <StyledLabel stacked>Vera Assist Preview</StyledLabel>
      <StyledFieldDescription stacked>
        Re-runs against the latest saved coaching and classifier prompts and the current transcript.
      </StyledFieldDescription>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, mb: 2 }}>
        <StyledButton
          startIcon={
            assistLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />
          }
          onClick={onRunAssist}
          disabled={!selectedContact || !hasInvestorMessage || !markdown.trim() || assistLoading}
        >
          Run Vera assist
        </StyledButton>
        <Typography variant="caption" color={promptSynced ? 'success.main' : 'text.secondary'}>
          {promptSynced ? 'Coaching prompt saved' : 'Saving coaching prompt…'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          ·
        </Typography>
        <Typography variant="caption" color={intentSynced ? 'success.main' : 'text.secondary'}>
          {intentSynced ? 'Classifier saved' : 'Saving classifier…'}
        </Typography>
      </Box>

      {assistError && (
        <StyledTypography tone="tone1" sx={{ color: 'error.main', mb: 2 }}>
          {assistError}
        </StyledTypography>
      )}

      {assistResult && (
        <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid #e3e8ef', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Answering {assistResult.targetMessageSenderName}
            </Typography>
            {assistResult.intentResult && (
              <Chip
                size="small"
                label={`Intent: ${assistResult.intentResult}`}
                color={INTENT_CHIP_COLOR[assistResult.intentResult] ?? 'default'}
                variant="outlined"
              />
            )}
          </Box>
          <Box
            sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}
          >
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {assistResult.targetMessageText}
            </Typography>
          </Box>
          <AdminAssistGroundedSuggestion
            messageId={`preview-assist-${sessionId}`}
            text={assistResult.suggestion}
            corpus={null}
            grounding={assistResult.googleSearchGrounding}
            markdownVariant="block"
          />
          <AdminAssistInvestorResponses
            responses={assistResult.investorResponses ?? []}
            onSelect={() => undefined}
            readOnly
          />
        </Box>
      )}
    </Box>
  );
}
